import { Fragment, useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import {
  PageContainer,
  ContentWrapper,
  TextContent,
  ErrorCode,
  AccessForbidden,
  Description,
  GoToDashboardButton,
  IllustrationContainer,
  Arch,
  Barrier,
  BarrierPost,
  BarrierTape,
  GroundLine,
  Grass,
} from './Styles';
import logger, { getErrorLogHistory } from 'utils/logger';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { USER_DATA } from 'shared/constants/users';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import localData from 'utils/localData';

const log = logger.createLogger('PageError');

const useSafeNavigate = () => {
  try {
    return useNavigate();
  } catch (_) {
    return (path) => {
      window.location.href = path;
    };
  }
};

const PageError = ({
  errorCode = 500,
  customErrorMessage = intl.get('shared_internal_server_error'),
  customErrorDescription = intl.get('shared_internal_server_error_description'),
  logoutFunction,
}) => {
  const navigate = useSafeNavigate();
  const [code, setCode] = useState(0);
  const [message, setMessage] = useState('');
  const [description, setDescription] = useState('');
  const userId = localStorage.getItem(USER_DATA.CURRENT_USER_ID);
  const organizationId = localStorage.getItem(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );
  const version = import.meta.env.VITE_APP_VERSION;

  const ERROR_CODES = {
    403: {
      code: 403,
      message: intl.get('shared_access_forbidden'),
      description: intl.get('shared_access_forbidden_description'),
    },
    404: {
      code: 404,
      message: intl.get('shared_page_not_found'),
      description: intl.get('shared_page_not_found_description'),
    },
    999: {
      code: 500,
      message: intl.get('shared_connection_error'),
      description: intl.get('shared_connection_error_description'),
    },
    998: {
      code: 500,
      message: intl.get('shared_no_employments'),
      description: intl.get('shared_no_employments_description'),
    },
  };

  const handleButtonClick = () => {
    if (errorCode === 998) {
      // For error code 998, use the logout function directly
      logoutFunction();
    } else {
      // For other error codes, try to navigate
      navigate('/dashboard/home');
    }
  };

  useEffect(() => {
    const decideMessage = async (errorCode, errorMessage, errorDescription) => {
      if (errorCode in ERROR_CODES) {
        setCode(ERROR_CODES[errorCode].code);
        setMessage(ERROR_CODES[errorCode].message);
        setDescription(ERROR_CODES[errorCode].description);
      } else {
        setCode(errorCode);
        setMessage(customErrorMessage);
        setDescription(customErrorDescription);
      }
      if (!(errorCode in ERROR_CODES)) {
        try {
          const logHistory = getErrorLogHistory();
          await api.post(`${DOLPHIN_API.LOGS}/error`, {
            params: {
              organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
            },
            data: {
              userID: userId,
              organizationID: organizationId,
              time: new Date().toISOString(),
              errorCode,
              errorMessage: `${errorMessage} - ${version}`,
              errorDescription,
              history: JSON.stringify(logHistory.slice(-3)),
            },
          });
        } catch (error) {
          log.error('Error sending error log:', error);
        }
      }
    };
    decideMessage(errorCode, customErrorMessage, customErrorDescription);
  }, [errorCode, customErrorMessage, customErrorDescription]);

  return (
    <Fragment>
      <PageContainer>
        <ContentWrapper>
          <TextContent>
            <ErrorCode>{code}</ErrorCode>
            <AccessForbidden>{message}</AccessForbidden>
            <Description>{description}</Description>
            <GoToDashboardButton onClick={handleButtonClick}>
              {errorCode === 998
                ? intl.get('dashboard_logout_modal_button_logout')
                : intl.get('shared_go_to_dashboard')}
            </GoToDashboardButton>
          </TextContent>
          <IllustrationContainer>
            <Arch />
            <Barrier>
              <BarrierPost />
              <BarrierTape>
                <span>{intl.get('shared_no_entry')}</span>
              </BarrierTape>
              <BarrierPost />
            </Barrier>
            <GroundLine />
            <Grass />
          </IllustrationContainer>
        </ContentWrapper>
      </PageContainer>
    </Fragment>
  );
};

PageError.propTypes = {
  errorCode: PropTypes.number,
  customErrorMessage: PropTypes.string,
  customErrorDescription: PropTypes.string,
  logoutFunction: PropTypes.func,
};

export default PageError;
